import { ReactNode, useCallback, useState } from 'react';
import styled from 'styled-components';

import GraphExplorerPanelHeaderRow from 'dashboard/components/GraphExplorerPanelHeaderRow';
import GraphExplorerPanelItem from 'dashboard/components/GraphExplorerPanelItem/GraphExplorerPanelItem';
import GraphExplorerPanelRow from 'dashboard/components/GraphExplorerPanelRow';
import { useGraphExplorer } from 'dashboard/components/GraphModule/hooks/useGraphExplorer';
import { parseLabelFromNodeId } from 'dashboard/lib/utils/graph';
import { DatumTypeToken, NodeId, nodeIdToHashable } from 'dashboard/models/graph';
import { HydratedNode } from 'dashboard/models/graph-hydration';
import { SegmentClient } from 'lib/clients/SegmentClient';
import { PERSONA_PROPERTY_DATUM_TYPES } from 'lib/constants/graph';

interface GraphPropertiesPanelItemProps {
  // Only used as a way to rerender for refreshes.
  renderCount: number;
}

const ScrollableContainer = styled('div')`
  max-height: 200px;
  overflow-y: auto;
  ::-webkit-scrollbar {
    width: 10px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: #d7d8df;
    border-radius: 8px;
    border: 2px solid white;
  }
`;

const ScrollableGraphExplorerPanelItem = styled('div')`
  overflow-y: auto;
  max-height: calc(100vh - 200px);
`;

const formatLabel = (
  getHydrationData: (nodeId: NodeId) => HydratedNode | null,
  nodeId: NodeId,
): ReactNode => {
  // If HydratedNode exists, parse from that to avoid possible JSON parsing.
  // Otherwise, try to parse from the nodeId value.
  const hydratedNode = getHydrationData(nodeId);
  if (!hydratedNode) {
    return parseLabelFromNodeId(nodeId);
  }
  switch (hydratedNode.nodeType) {
    case 'address': {
      const line1 = hydratedNode.hydrationData['address-line1'];
      const line2 = hydratedNode.hydrationData['address-line2'];
      const line3 = hydratedNode.hydrationData['address-line3'];
      return [line1, line2, line3].filter(Boolean).join(', ');
    }
    case 'name': {
      const firstName = hydratedNode.hydrationData['name-first'];
      const middleName = hydratedNode.hydrationData['name-middle'];
      const lastName = hydratedNode.hydrationData['name-last'];

      return [firstName, middleName, lastName].filter(Boolean).join(' ');
    }
    case 'id_document_number': {
      return hydratedNode.hydrationData['document-number'];
    }
    // fine to just parse the node value directly
    default:
      return parseLabelFromNodeId(nodeId);
  }
};

const GraphPropertiesPanelItem = (_props: GraphPropertiesPanelItemProps) => {
  const { graphExplorer } = useGraphExplorer();
  const {
    dimDatumTypeNodes,
    dimNode,
    getCollapsedGroupNodeBelongsTo,
    getCollapsedPairwiseNodeNodeBelongsTo,
    getNodesByDatumType,
    getHydrationData,
    getNodeRenderConfig,
    getNodeDatumTypes,
    highlightNode,
    isDatumTypeNodesSelected,
    isDatumTypeDimmed,
    isDimmed,
    isHighlighted,
    isNodeSelected,
    selectDatumTypeNodes,
    selectNode,
    setDimmedProperty,
  } = graphExplorer;
  const [hoveredDatumTypeHeader, setHoveredDatumTypeHeader] = useState(null);
  const allDatumTypes = PERSONA_PROPERTY_DATUM_TYPES.concat(getNodeDatumTypes());

  const nodeDisplayNameLookupFn = (token: DatumTypeToken): string => {
    return getNodeRenderConfig(token).displayName;
  };

  const handleDatumTypeHeaderClick = useCallback(
    (datumTypeToken: DatumTypeToken, selected: boolean) => {
      selected
        ? SegmentClient.sendSegmentTrackEvent('Graph - Select Group')
        : SegmentClient.sendSegmentTrackEvent('Graph - Deselect Group');
      return selectDatumTypeNodes(datumTypeToken, selected);
    },

    [selectDatumTypeNodes],
  );
  const handleDatumTypeHeaderDimClick = useCallback(
    (datumTypeToken: DatumTypeToken, dimmed: boolean) => {
      // Don't show highlight for dimmed nodes
      for (const nodeId of Array.from(getNodesByDatumType(datumTypeToken))) {
        if (dimmed) {
          highlightNode(nodeId, false);
        }
      }
      dimmed
        ? SegmentClient.sendSegmentTrackEvent('Graph - Dim Group')
        : SegmentClient.sendSegmentTrackEvent('Graph - Undim Group');
      return dimDatumTypeNodes(datumTypeToken, dimmed);
    },
    [dimDatumTypeNodes, getNodesByDatumType, highlightNode],
  );
  const handleDatumTypeHeaderHover = useCallback(
    (datumTypeToken: DatumTypeToken, hover: boolean) => {
      if (hover) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setHoveredDatumTypeHeader(datumTypeToken);
      } else {
        setHoveredDatumTypeHeader(null);
      }

      // Don't show highlight for dimmed nodes
      Array.from(getNodesByDatumType(datumTypeToken)).forEach((nodeId) => {
        // Only update highlight if node is undimmed OR we are unhighlighting a dimmed node to clean it up
        if (!isDimmed(nodeId) || (isDimmed(nodeId) && !hover)) {
          highlightNode(nodeId, hover);
        }
      });
      hover
        ? SegmentClient.sendSegmentTrackEvent('Graph - Hover Group')
        : SegmentClient.sendSegmentTrackEvent('Graph - Unhover Group');
    },
    [getNodesByDatumType, highlightNode, isDimmed],
  );

  const handleNodeRowClick = useCallback(
    (nodeId: NodeId, selected: boolean) => {
      selectNode(nodeId, selected);
      selected
        ? SegmentClient.sendSegmentTrackEvent('Graph - Select Individual')
        : SegmentClient.sendSegmentTrackEvent('Graph - Deselect Individual');
    },
    [selectNode],
  );
  const handleNodeRowDimClick = useCallback(
    (nodeId: NodeId, dimmed: boolean) => {
      // if a node is undimmed, undim the corresponding property as well
      if (!dimmed) {
        setDimmedProperty(nodeId.token, dimmed);
      }
      dimNode(nodeId, dimmed);
      if (isHighlighted(nodeId)) {
        highlightNode(nodeId, false);
      }
      dimmed
        ? SegmentClient.sendSegmentTrackEvent('Graph - Dim Individual')
        : SegmentClient.sendSegmentTrackEvent('Graph - Undim Individual');
    },
    [dimNode, highlightNode, isHighlighted, setDimmedProperty],
  );
  const handleNodeRowHover = useCallback(
    (nodeId: NodeId, hovered: boolean) => {
      // Don't show highlight for dimmed nodes
      if (!isDimmed(nodeId)) {
        highlightNode(nodeId, hovered);
      }
      hovered
        ? SegmentClient.sendSegmentTrackEvent('Graph - Hover Individual')
        : SegmentClient.sendSegmentTrackEvent('Graph - Unhover Individual');
    },
    [highlightNode, isDimmed],
  );
  const numNodes = graphExplorer.getNodeIds().length;

  return (
    <ScrollableGraphExplorerPanelItem>
      <GraphExplorerPanelItem label='Properties'>
        {numNodes === 0 ? (
          <>No properties to show.</>
        ) : (
          allDatumTypes
            .filter((type) => {
              // TODO (lewis) Filter out unknown datum types at the source.
              // The data loader should know how to filter these out before they
              // get to the UI rendering.
              return getNodeRenderConfig(type.token).displayName !== 'Unknown';
            })
            .map((type) => {
              // filter out nodes part of collapsed groups
              const nodeIdsOfType = getNodesByDatumType(type.token).filter((nodeId) => {
                return (
                  getCollapsedGroupNodeBelongsTo(nodeId) == null &&
                  getCollapsedPairwiseNodeNodeBelongsTo(nodeId) == null
                );
              });

              return (
                nodeIdsOfType.length > 0 && (
                  <GraphExplorerPanelHeaderRow
                    key={type.token}
                    label={`${nodeDisplayNameLookupFn(type.token)} (${nodeIdsOfType.length})`}
                    selected={isDatumTypeNodesSelected(type.token)}
                    dimmed={isDatumTypeDimmed(type.token)}
                    datumTypeToken={type.token}
                    handleClick={handleDatumTypeHeaderClick}
                    handleDimClick={handleDatumTypeHeaderDimClick}
                    handleHover={handleDatumTypeHeaderHover}
                    getNodeRenderConfig={getNodeRenderConfig}
                    scrollable={nodeIdsOfType.length > 6}
                  >
                    <ScrollableContainer>
                      {nodeIdsOfType?.map((nodeId) => {
                        return (
                          <GraphExplorerPanelRow
                            nodeId={nodeId}
                            key={nodeIdToHashable(nodeId)}
                            label={formatLabel(getHydrationData, nodeId)}
                            selected={isNodeSelected(nodeId)}
                            dimmed={isDimmed(nodeId)}
                            highlighted={isHighlighted(nodeId) && !hoveredDatumTypeHeader}
                            handleClick={handleNodeRowClick}
                            handleDimClick={handleNodeRowDimClick}
                            handleHover={handleNodeRowHover}
                            datumTypeToken={type.token}
                            getNodeRenderConfig={getNodeRenderConfig}
                          />
                        );
                      })}
                    </ScrollableContainer>
                  </GraphExplorerPanelHeaderRow>
                )
              );
            })
        )}
      </GraphExplorerPanelItem>
    </ScrollableGraphExplorerPanelItem>
  );
};

export default GraphPropertiesPanelItem;
